/**
 * "What you'll get" template preview modal — shared by every creation surface
 * (Content Planner wizard, stepped wizard, Blog Command Center, template gallery).
 * Shows the template's generation config read-only, with an optional "Use this
 * template" action. No backend call — reads default_config off the summary.
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { AutoTemplateConfigPreview } from './AutoTemplateConfigPreview';

interface TemplatePreviewModalProps {
  template: BlogTemplateSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional — when provided, shows a "Use this template" button. */
  onUse?: (templateId: string) => void;
  /** True when this template is already the selected one. */
  isSelected?: boolean;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  open,
  onOpenChange,
  onUse,
  isSelected = false,
}) => {
  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {template.icon && <span className="text-xl leading-none">{template.icon}</span>}
            {template.name}
          </DialogTitle>
          {template.description && (
            <DialogDescription>{template.description}</DialogDescription>
          )}
        </DialogHeader>

        {template.features?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {template.features.slice(0, 6).map((f) => (
              <Badge key={f} variant="secondary" className="text-[11px] font-normal">
                {f}
              </Badge>
            ))}
          </div>
        )}

        <AutoTemplateConfigPreview config={template.default_config} />

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onUse && (
            <Button
              onClick={() => {
                onUse(template.id);
                onOpenChange(false);
              }}
              disabled={isSelected}
              className="bg-coral text-white hover:bg-coral-hover"
            >
              {isSelected ? 'Selected' : 'Use this template'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
